import React from 'react';
import {
    ProofOfIdVerificationContextConsumer,
    ProofOfIdVerificationContextProvider,
} from '../../../../contexts/proof-of-id-verification/ProofOfIdVerificationContext';
import { useStore } from '../../../../hooks/useStore';
import { getBadgeProps } from '../../../../services/kyc';
import { translate } from '../../../../services/translate';
import { stores } from '../../../../stores';
import AccountVerificationDocumentCapture from '../document-capture/AccountVerificationDocumentCapture';
import AccountVerificationIdenfy from '../idenfy/AccountVerificationIdenfy';
import AccountVerificationMultiStepBox from '../multi-step/box/AccountVerificationMultiStepBox';
import AccountVerificationProofOfIdSteps from './steps/AccountVerificationProofOfIdSteps';

interface Props {
    isCompactView?: boolean;
    onCompleted?: () => void;
    title?: string;
    badgeText?: string;
    className?: string;
}

export default function AccountVerificationProofOfId({ onCompleted, ...rest }: Props) {
    const [kycSettings] = useStore(stores.kyc.settings);

    if (kycSettings.isIdenfyEnabled) {
        return <AccountVerificationIdenfy onCompleted={onCompleted} {...rest} />;
    }

    if (kycSettings.isKycCaptureEnabled) {
        return <AccountVerificationDocumentCapture onCompleted={onCompleted} {...rest} />;
    }

    return (
        <ProofOfIdVerificationContextProvider onCompleted={onCompleted}>
            <ProofOfIdVerificationContextConsumer>
                {({ activeStep, documentUpload, setActiveStep, stepCompletionState, formContainerRef }) => {
                    const badgeProps =
                        activeStep > 0
                            ? { badgeText: translate('In progress', 'ui.common'), badgeColor: 'info' }
                            : getBadgeProps(documentUpload.status);
                    const progressIndicatorSteps = [
                        {
                            description: translate('type', 'ui.account.kyc'),
                            isCompleted: stepCompletionState[1],
                        },
                        {
                            description: translate('documents', 'ui.account.kyc'),
                            isCompleted: stepCompletionState[2],
                        },
                        {
                            description: translate('review', 'ui.account.kyc'),
                            isCompleted: stepCompletionState[3],
                        },
                        {
                            description: translate('submit', 'ui.account.kyc'),
                            isCompleted: stepCompletionState[4],
                        },
                    ];
                    return (
                        <AccountVerificationMultiStepBox
                            Steps={AccountVerificationProofOfIdSteps}
                            activeStep={activeStep}
                            progressIndicatorSteps={progressIndicatorSteps}
                            ref={formContainerRef}
                            setActiveStep={setActiveStep}
                            title={translate('proof-of-id', 'ui.account')}
                            dataTest="proof-of-id"
                            {...badgeProps}
                            {...rest}
                        />
                    );
                }}
            </ProofOfIdVerificationContextConsumer>
        </ProofOfIdVerificationContextProvider>
    );
}
